/**
 * Attachment plumbing for RFQ query threads, kept out of QueryThread.tsx so
 * the component stays under the 500-line limit.
 *
 * Uploads create an RFQDocument (customer-only on the server); downloads must
 * come through the authenticated endpoint because `/media/rfq/**` is
 * hard-blocked as private media.
 */
import { rfqAPI } from '../../lib/api';
import type { RFQDocument } from '../../lib/api';

/** Rejected before upload so the user hears about it immediately, not after a 413. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Upload each file as an RFQDocument on `rfqId` and return the new ids.
 * Sequential rather than parallel: a failure part-way through must leave the
 * message unsent with the reason named, not half-attached.
 */
export async function uploadQueryAttachments(rfqId: number, files: File[]): Promise<number[]> {
  const ids: number[] = [];
  for (const file of files) {
    const form = new FormData();
    form.append('rfq', String(rfqId));
    form.append('file', file);
    form.append('document_type', 'other');
    form.append('title', file.name);
    // A boolean missing from multipart reads as False server-side.
    form.append('is_active', 'true');
    const response = await rfqAPI.uploadDocument(form);
    ids.push(response.data.id);
  }
  return ids;
}

/** Fetch the private file as a blob and hand it to the browser as a save. */
export async function downloadQueryAttachment(doc: RFQDocument): Promise<void> {
  const response = await rfqAPI.downloadDocument(doc.id);
  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = doc.file_name || doc.title || 'attachment';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
